import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Divider,
  Chip,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Business as BusinessIcon,
  Support as SupportIcon,
  Send as SendIcon,
  Language as LanguageIcon
} from '@mui/icons-material';

interface ContactModalProps {
  open: boolean;
  onClose: () => void;
}

const ContactModal: React.FC<ContactModalProps> = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('sending');

    try {
      // Here you would integrate with your contact form service
      // For now, we'll simulate a successful submission
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSubmitStatus('sent');

      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({ name: '', email: '', company: '', message: '' });
        setSubmitStatus('idle');
      }, 3000);
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  const contactInfo = [
    {
      icon: <EmailIcon sx={{ color: '#4A90C2' }} />,
      title: 'Email Support',
      primary: 'support@rafilis.com',
      secondary: 'Get help with technical questions and account issues',
      action: () => window.open('mailto:support@rafilis.com', '_blank')
    },
    {
      icon: <BusinessIcon sx={{ color: '#2E86AB' }} />,
      title: 'Sales Inquiries',
      primary: 'sales@rafilis.com',
      secondary: 'Enterprise solutions and custom implementations',
      action: () => window.open('mailto:sales@rafilis.com', '_blank')
    },
    {
      icon: <PhoneIcon sx={{ color: '#5BA3D4' }} />,
      title: 'Phone Support',
      primary: '+1 (555) 123-4567',
      secondary: 'Available 24/7 for Pro subscribers',
      action: () => window.open('tel:+15551234567', '_blank')
    },
    {
      icon: <LocationIcon sx={{ color: '#4A90C2' }} />,
      title: 'Headquarters',
      primary: 'Istanbul, Turkey',
      secondary: 'European operations and development center',
      action: null
    }
  ];

  const supportOptions = [
    {
      title: 'Technical Documentation',
      description: 'Comprehensive guides and API documentation',
      link: '#'
    },
    {
      title: 'Video Tutorials',
      description: 'Step-by-step setup and configuration videos',
      link: '#'
    },
    {
      title: 'Community Forum',
      description: 'Connect with other users and share experiences',
      link: '#'
    },
    {
      title: 'Live Chat Support',
      description: 'Real-time assistance for Pro subscribers',
      link: '#'
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: '#ffffff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SupportIcon sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Contact Rafilis
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              We're here to help with your audio management needs
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#ffffff',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Contact Information Section */}
        <Box sx={{ p: 4, background: '#ffffff' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1e293b' }}>
            Get in Touch
          </Typography>
          <Grid container spacing={3}>
            {contactInfo.map((contact, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{
                  height: '100%',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)',
                  border: '1px solid rgba(74, 144, 194, 0.1)',
                  transition: 'all 0.3s ease',
                  cursor: contact.action ? 'pointer' : 'default',
                  '&:hover': {
                    transform: contact.action ? 'translateY(-2px)' : 'none',
                    boxShadow: contact.action ? '0 8px 25px rgba(74, 144, 194, 0.15)' : 'none',
                    border: '1px solid rgba(74, 144, 194, 0.3)'
                  }
                }}
                onClick={contact.action || undefined}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {contact.icon}
                      <Typography variant="h6" sx={{ ml: 1.5, fontWeight: 600, color: '#1e293b' }}>
                        {contact.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#4A90C2', mb: 1 }}>
                      {contact.primary}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.5 }}>
                      {contact.secondary}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider />

        {/* Contact Form Section */}
        <Box sx={{ p: 4, background: '#f8fafc' }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1e293b' }}>
                Send us a Message
              </Typography>

              {submitStatus === 'sent' && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  Thank you! Your message has been sent successfully. We'll get back to you soon.
                </Alert>
              )}

              {submitStatus === 'error' && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  Sorry, there was an error sending your message. Please try again or contact us directly.
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Company/Organization"
                  value={formData.company}
                  onChange={handleInputChange('company')}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Message"
                  multiline
                  rows={4}
                  value={formData.message}
                  onChange={handleInputChange('message')}
                  required
                  sx={{ mb: 3 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SendIcon />}
                  disabled={submitStatus === 'sending' || submitStatus === 'sent'}
                  sx={{
                    background: 'linear-gradient(135deg, #4A90C2, #2E86AB)',
                    py: 1.5,
                    px: 4,
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2E86AB, #1a5f7a)'
                    }
                  }}
                >
                  {submitStatus === 'sending' ? 'Sending...' :
                   submitStatus === 'sent' ? 'Message Sent!' :
                   'Send Message'}
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1e293b' }}>
                Support Resources
              </Typography>
              {supportOptions.map((option, index) => (
                <Card key={index} sx={{
                  mb: 2,
                  background: '#ffffff',
                  border: '1px solid rgba(74, 144, 194, 0.1)',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    border: '1px solid rgba(74, 144, 194, 0.3)',
                    transform: 'translateX(4px)'
                  }
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
                      {option.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      {option.description}
                    </Typography>
                  </CardContent>
                </Card>
              ))}

              {/* Business Hours */}
              <Box sx={{
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                borderRadius: '12px',
                p: 3,
                color: '#ffffff',
                mt: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TimeIcon sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Business Hours
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                  Monday - Friday: 9:00 AM - 6:00 PM (GMT+3)<br />
                  Saturday: 10:00 AM - 4:00 PM (GMT+3)<br />
                  Sunday: Closed<br />
                  <br />
                  <strong>Emergency Support:</strong> 24/7 for Pro subscribers
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label="Istanbul, Turkey"
                    size="small"
                    sx={{ background: 'rgba(255,255,255,0.1)', color: '#ffffff' }}
                  />
                  <Chip
                    label="GMT+3"
                    size="small"
                    sx={{ background: 'rgba(255,255,255,0.1)', color: '#ffffff' }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Company Information */}
        <Box sx={{
          p: 4,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: '#ffffff',
          textAlign: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Rafilis Audio Solutions
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, mb: 3, maxWidth: 600, mx: 'auto' }}>
            Leading provider of professional audio management systems for hotels, restaurants, and venues worldwide.
            Trusted by hundreds of businesses for reliable, scalable audio solutions.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Chip
              label="Founded in 2020"
              sx={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                fontWeight: 500
              }}
            />
            <Chip
              label="500+ Venues Served"
              sx={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                fontWeight: 500
              }}
            />
            <Chip
              label="24/7 Enterprise Support"
              sx={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                fontWeight: 500
              }}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;